import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CalendarBlank, MapPin, Trophy, ShieldCheck, Leaf } from "@phosphor-icons/react";

const PRIVACY_MODES = [
  { key: "exact", label: "Exact", hint: "Full GPS coordinates visible to everyone." },
  { key: "approximate", label: "Approximate", hint: "Coordinates rounded to ~1km." },
  { key: "city", label: "City", hint: "City + state + country only." },
  { key: "region", label: "Region", hint: "State + country only." },
  { key: "country", label: "Country", hint: "Country only." },
  { key: "private", label: "Private", hint: "Location fully hidden from the public archive." },
];

function BadgeCard({ b }) {
  return (
    <div
      data-testid={`diary-badge-${b.id}`}
      className={`rounded-xl border p-4 flex flex-col gap-2 transition ${
        b.earned
          ? "border-[#B85F2A] bg-[#FFF6E5]"
          : "border-black/10 bg-white opacity-70"
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="text-2xl">{b.emoji}</span>
        <div className="font-serif-display text-lg leading-tight">{b.name}</div>
        {b.earned && (
          <span className="ml-auto rounded-full bg-[#B85F2A] text-white text-[9px] font-mono-tech tracking-widest uppercase px-2 py-0.5">Earned</span>
        )}
      </div>
      <div className="text-xs text-[#3A3830]/70">{b.description}</div>
      <div className="mt-1">
        <div className="h-1.5 rounded-full bg-black/10 overflow-hidden">
          <div className="h-full bg-[#B85F2A]" style={{ width: `${b.progress_pct}%` }} />
        </div>
        <div className="mt-1 font-mono-tech text-[9px] tracking-widest uppercase text-[#3A3830]/60">
          {b.earned ? "Milestone reached" : `${b.value} / ${b.threshold}`}
        </div>
      </div>
    </div>
  );
}

function TimelineEntry({ e }) {
  const when = (e.observed_at || e.event_date || e.created_at || "").slice(0, 10);
  return (
    <div className="flex gap-4 items-start pb-4 border-b border-black/5" data-testid={`diary-entry-${e.id}`}>
      {e.cover_image ? (
        <img src={e.cover_image} alt="" className="w-24 h-24 rounded-lg object-cover shrink-0" />
      ) : (
        <div className="w-24 h-24 rounded-lg bg-[#F5EBDC] flex items-center justify-center shrink-0">
          <Leaf size={24} className="text-[#B85F2A]/60" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#3A3830]/60">
          <span>{when}</span>
          {e.category && <span>· {e.category}</span>}
          {e.moderation_status && <span>· {e.moderation_status}</span>}
        </div>
        <div className="font-serif-display text-lg leading-tight text-[#241209] line-clamp-2">
          {e.title || e.headline || "Untitled entry"}
        </div>
        {e.summary && (
          <div className="text-sm text-[#3A3830]/80 line-clamp-2 mt-1">{e.summary}</div>
        )}
        {e.location?.city || e.location?.state ? (
          <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-[#3A3830]/60">
            <MapPin size={10} /> {[e.location.city, e.location.state, e.location.country].filter(Boolean).join(", ")}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function PrivacyPanel({ current, onChange }) {
  const [busy, setBusy] = useState(false);
  const set = async (mode) => {
    if (mode === current) return;
    setBusy(true);
    try {
      const { data } = await http.post("/auth/me/location-privacy", { location_privacy: mode });
      onChange?.(data.location_privacy);
      toast.success(`Location privacy: ${mode}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-black/10 bg-white p-4 space-y-3" data-testid="diary-privacy">
      <div className="flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]/70">
        <ShieldCheck size={14} weight="fill" /> Location privacy
      </div>
      <div className="text-xs text-[#3A3830]/70">
        Choose how precisely your locations appear to the public. This affects
        every one of your stories immediately across the network. New accounts
        default to <b>City</b> — a safe coarse level.
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {PRIVACY_MODES.map((m) => (
          <button
            key={m.key}
            data-testid={`diary-privacy-${m.key}`}
            disabled={busy}
            onClick={() => set(m.key)}
            className={`text-left rounded-lg border px-3 py-2 transition ${
              current === m.key
                ? "border-[#B85F2A] bg-[#FFF6E5]"
                : "border-black/10 bg-white hover:border-black/30"
            } disabled:opacity-40`}
          >
            <div className="font-serif-display text-sm">{m.label}</div>
            <div className="text-[10px] text-[#3A3830]/60 mt-0.5">{m.hint}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function NatureDiaryPanel() {
  const { user, refresh } = useAuth();
  const [timeline, setTimeline] = useState(null);
  const [badges, setBadges] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [privacy, setPrivacy] = useState(user?.location_privacy || "city");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, b, s] = await Promise.all([
        http.get("/nature/diary/timeline", { params: { limit: 50 } }),
        http.get("/nature/diary/badges"),
        http.get("/nature/diary/stats"),
      ]);
      setTimeline(t.data);
      setBadges(b.data);
      setStats(s.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load diary"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (user?.location_privacy) setPrivacy(user.location_privacy); }, [user]);

  if (!user) return null;

  return (
    <motion.section
      data-testid="diary-panel"
      className="pb-16 px-6 md:px-10 max-w-[1600px] mx-auto"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
    >
      <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-brand)] mb-3">
        / My Nature Diary
      </div>
      <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight">Your living record.</h2>

      {/* Stats strip */}
      {stats && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="diary-stats">
          {[
            { label: "Total entries", value: stats.stats.total },
            { label: "Approved", value: stats.stats.approved },
            { label: "Species", value: stats.stats.unique_species },
            { label: "Categories", value: stats.stats.unique_categories },
            { label: "Active days", value: stats.stats.unique_days },
          ].map((x) => (
            <div key={x.label} className="rounded-xl border border-black/10 bg-white p-3">
              <div className="font-serif-display text-3xl leading-none">{x.value}</div>
              <div className="mt-1 font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]/60">{x.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 grid lg:grid-cols-[1fr_360px] gap-8">
        {/* Timeline */}
        <div>
          <div className="flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]/70 mb-4">
            <CalendarBlank size={14} weight="fill" /> Timeline
          </div>
          {loading ? (
            <div className="text-xs text-[#3A3830]/70">Loading…</div>
          ) : !timeline || timeline.total === 0 ? (
            <div className="rounded-xl border border-dashed border-black/15 p-8 text-center text-[#3A3830]/70 text-sm">
              You haven't contributed any stories yet. Head to the map or "Add sighting" to start your diary.
            </div>
          ) : (
            <div className="space-y-8" data-testid="diary-timeline">
              {timeline.months.map((m) => (
                <div key={m.month}>
                  <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand)] mb-3">
                    {m.month} · {m.count} entr{m.count === 1 ? "y" : "ies"}
                  </div>
                  <div className="space-y-4">
                    {m.entries.map((e) => <TimelineEntry key={e.id} e={e} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar: badges + privacy */}
        <div className="space-y-8">
          <div>
            <div className="flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]/70 mb-4">
              <Trophy size={14} weight="fill" /> Badges
              {badges && <span className="ml-auto opacity-80 tracking-normal">{badges.earned_count}/{badges.total_count} earned</span>}
            </div>
            {badges ? (
              <div className="grid grid-cols-1 gap-3" data-testid="diary-badges">
                {badges.badges.map((b) => <BadgeCard key={b.id} b={b} />)}
              </div>
            ) : (
              <div className="text-xs text-[#3A3830]/70">Loading…</div>
            )}
          </div>

          <PrivacyPanel
            current={privacy}
            onChange={(next) => { setPrivacy(next); refresh?.(); }}
          />
        </div>
      </div>
    </motion.section>
  );
}
